import React, { useEffect, useMemo, useState } from "react";
import styles from "./RouteIndex.module.css";
import TimelineHeader from "./TimelineHeader";
import TimelineSidebar from "./TimelineSidebar";
import TimelineCanvas from "./TimelineCanvas";

export default function RouteTimeline({
  route,
  selectedReceipt,
  onSelectReceipt,
  onPersonClick,
}) {
  /** === Настройки отображения === */
  const [collapsedMode, setCollapsedMode] = useState("status"); // status | scan
  const [movementMode, setMovementMode] = useState("both"); // travel | stop | both
  const [sortMode, setSortMode] = useState("status"); // movement | status | scan
  const [win, setWin] = useState({ start: 0, end: 24 });
  const span = win.end - win.start;

  /** === Подготовка enriched данных === */
const receipts = useMemo(() => {
  const base = route?.receipts || [];
  if (!base.length) return [];

  // === базовая сортировка ===
  const sorted = [...base].sort((a, b) => {
    const getSortTime = (r) => {
      if (sortMode === "scan") return new Date(r.marks_changed_at || 0);
      if (sortMode === "status") return new Date(r.status_changed_at || 0);
      return new Date(r.departed_at || r.arrived_at || 0); // movement
    };
    return getSortTime(a) - getSortTime(b);
  });

  // === enrich ===
  return sorted.map((r, i) => {
    const arrived = r.arrived_at ? new Date(r.arrived_at) : null;
    const departed = r.departed_at ? new Date(r.departed_at) : null;

    // 🟡 ищем самую позднюю marks_changed_at среди продуктов
    let latestMark = null;
    if (Array.isArray(r.products) && r.products.length) {
      for (const p of r.products) {
        if (p.marks_changed_at) {
          const t = new Date(p.marks_changed_at);
          if (!latestMark || t > latestMark) latestMark = t;
        }
      }
    }

    // 🟡 если нет products, оставляем старое поле
    const marks_changed_at = latestMark || (r.marks_changed_at ? new Date(r.marks_changed_at) : null);

    // ищем ближайший departure после arrival
    let next_departure = null;
    if (arrived) {
      for (let j = i + 1; j < sorted.length; j++) {
        const dep = sorted[j].departed_at ? new Date(sorted[j].departed_at) : null;
        if (dep && dep > arrived) {
          next_departure = dep;
          break;
        }
      }
    }

    if (departed && arrived) {
      const travelMin = Math.round((arrived - departed) / 60000);
      const stopMin = next_departure ? Math.round((next_departure - arrived) / 60000) : "-";
      const nextStart = next_departure ? next_departure.toLocaleTimeString() : "-";
      console.log(
        `#${r.id ?? i} | 🚚 В пути: ${travelMin} мин (${departed.toLocaleTimeString()}–${arrived.toLocaleTimeString()}) | ⏸ Стоянка: ${stopMin} мин (до ${nextStart})`
      );
    }

    return {
      ...r,
      arrived,
      departed,
      next_departure,
      hasTravel: !!(arrived && departed),
      hasStop: !!(arrived && next_departure),
      marks_changed_at, // ⬅️ добавляем новое поле
    };
  });
}, [route, sortMode]);


  /** === Автоматический диапазон часов === */
  useEffect(() => {
    const hours = [];
    receipts.forEach((r) => {
      const t1 = r.status_changed_at ? new Date(r.status_changed_at) : null;
      const t2 = r.marks_changed_at ? new Date(r.marks_changed_at) : null;
      if (t1 && !isNaN(t1)) hours.push(t1.getHours() + t1.getMinutes() / 60);
      if (t2 && !isNaN(t2)) hours.push(t2.getHours() + t2.getMinutes() / 60);
    });
    if (!hours.length) return;
    const pad = 0.5;
    const minH = Math.max(0, Math.floor(Math.min(...hours)) - pad);
    const maxH = Math.min(24, Math.ceil(Math.max(...hours)) + pad);
    setWin({ start: minH, end: maxH });
  }, [receipts]);

  const fitToData = () => {
    const hours = [];
    receipts.forEach((r) => {
      const t1 = r.status_changed_at ? new Date(r.status_changed_at) : null;
      const t2 = r.marks_changed_at ? new Date(r.marks_changed_at) : null;
      if (t1 && !isNaN(t1)) hours.push(t1.getHours() + t1.getMinutes() / 60);
      if (t2 && !isNaN(t2)) hours.push(t2.getHours() + t2.getMinutes() / 60);
    });
    if (!hours.length) {
      setWin({ start: 0, end: 24 });
      return;
    }
    const pad = 0.5;
    const minH = Math.max(0, Math.floor(Math.min(...hours)) - pad);
    const maxH = Math.min(24, Math.ceil(Math.max(...hours)) + pad);
    setWin({ start: minH, end: maxH });
  };

  /** === Рендер === */
  return (
    <div className={styles.timelineCard}>
      <TimelineHeader
        route={route}
        onPersonClick={onPersonClick}
        collapsedMode={collapsedMode}
        setCollapsedMode={setCollapsedMode}
        movementMode={movementMode}
        setMovementMode={setMovementMode}
        sortMode={sortMode}
        setSortMode={setSortMode}
        onReset={() => setWin({ start: 0, end: 24 })}
        onFit={fitToData}
        win={win}
      />

      <div className={styles.mainRow}>
        <div className={styles.scrollArea}>
          <TimelineSidebar
            receipts={receipts}
            selectedReceipt={selectedReceipt}
            onSelectReceipt={onSelectReceipt}
          />
          <TimelineCanvas
            receipts={receipts}
            collapsedMode={collapsedMode}
            movementMode={movementMode}
            win={win}
            span={span}
            selectedReceipt={selectedReceipt}
            onSelectReceipt={onSelectReceipt}
            onWinChange={setWin}
          />
        </div>
      </div>
    </div>
  );
}
